import type { DentalProgram, ProfileInputs } from '../types';

export interface DatScoreSummary {
  scale: 'new-200-600' | 'old-1-30';
  academicAverage: number | null;
  totalScience: number | null;
  scores: number[];
}

const NEW_DAT_SCORE = '(?:[2-5]\\d0|600)';
const OLD_DAT_SCORE = '(?:[1-9]|[12]\\d|30)';
const ANY_DAT_SCORE = `(?:${NEW_DAT_SCORE}|${OLD_DAT_SCORE})`;

function profileText(inputs: ProfileInputs | null | undefined): string {
  if (!inputs) return '';
  return Object.values(inputs).filter(Boolean).join('\n');
}

function numbersFrom(text: string, pattern: string): number[] {
  const re = new RegExp(`\\b${pattern}\\b`, 'g');
  return [...text.matchAll(re)].map((m) => Number(m[0])).filter(Number.isFinite);
}

function isNewScale(scores: number[]): boolean {
  return scores.some((n) => n >= 200);
}

function scaleOf(scores: number[]): DatScoreSummary['scale'] {
  return isNewScale(scores) ? 'new-200-600' : 'old-1-30';
}

function findLabeledScore(text: string, labels: string[]): number | null {
  const normalized = text.replace(/\s+/g, ' ');
  const label = labels.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`\\b(?:${label})\\b[^\\d]{0,35}(${ANY_DAT_SCORE})\\b`, 'i');
  const match = normalized.match(re);
  return match ? Number(match[1]) : null;
}

export function extractDatScoreSummary(text: string): DatScoreSummary | null {
  if (!/\bDAT\b|academic\s+average|total\s+science/i.test(text)) return null;

  const normalized = text.replace(/\r/g, ' ');
  const tableRow = normalized.match(
    new RegExp(
      `\\b\\d{1,2}[-/]\\d{1,2}[-/]\\d{2,4}\\b\\s+((?:${NEW_DAT_SCORE}\\s+){5,9}${NEW_DAT_SCORE})`,
      'i',
    ),
  );

  if (tableRow) {
    const scores = numbersFrom(tableRow[1], NEW_DAT_SCORE);
    return {
      scale: 'new-200-600',
      academicAverage: scores[0] ?? null,
      totalScience: scores[7] ?? null,
      scores,
    };
  }

  const academicAverage = findLabeledScore(normalized, ['academic average', 'AA']);
  const totalScience = findLabeledScore(normalized, ['total science', 'TS']);
  const scopedScores = numbersFrom(normalized, ANY_DAT_SCORE).filter((n) => n <= 30 || n >= 200);
  const scores = Array.from(new Set([academicAverage, totalScience, ...scopedScores].filter((n): n is number => n != null)));

  if (!scores.length) return null;
  return {
    scale: scaleOf(scores),
    academicAverage,
    totalScience,
    scores,
  };
}

export function extractDatScoreSummaryFromInputs(inputs: ProfileInputs | null | undefined): DatScoreSummary | null {
  return extractDatScoreSummary(profileText(inputs));
}

export function isGenerallyCompetitiveDat(summary: DatScoreSummary): boolean {
  const aa = summary.academicAverage;
  const ts = summary.totalScience;
  if (aa == null) return false;

  if (summary.scale === 'new-200-600') {
    return aa >= 430 && (ts == null || ts >= 420);
  }

  return aa >= 20 && (ts == null || ts >= 19);
}

function datAverageFromText(text: string): number | null {
  const direct = findLabeledScore(text, ['academic average', 'average DAT', 'mean DAT', 'median DAT', 'AA']);
  if (direct != null) return direct;

  const match = text.match(new RegExp(`\\bDAT\\b[^.\\n]{0,60}\\b(?:average|mean|median)\\b[^\\d]{0,20}(${ANY_DAT_SCORE})\\b`, 'i'));
  return match ? Number(match[1]) : null;
}

export function extractSchoolDatAverage(program: DentalProgram | null | undefined): number | null {
  if (!program) return null;
  const fit = program.fit;
  const text = [
    fit.mission,
    fit.curriculumStyle,
    fit.clinicalEmphasis,
    fit.communityServiceFocus,
    fit.researchFocus,
    fit.location,
    fit.uniquePrograms,
    fit.interviewStyle,
    ...fit.commonQuestionThemes,
  ].join('\n');
  return datAverageFromText(text);
}

function comparableApplicantAverage(summary: DatScoreSummary, comparison: number): number | null {
  if (summary.academicAverage == null) return null;
  if (comparison >= 100 && summary.scale === 'new-200-600') return summary.academicAverage;
  if (comparison <= 30 && summary.scale === 'old-1-30') return summary.academicAverage;
  return null;
}

function isDatConcern(flag: string): boolean {
  return /\bDAT\b|academic\s+average|total\s+science|\bAA\b|\bTS\b/i.test(flag);
}

function preserveAsRetakeConcern(flag: string): boolean {
  return /retak|multiple\s+attempt|attempts/i.test(flag) && !/low|below|weak|score|average|under/i.test(flag);
}

export function filterResolvedDatRedFlags(
  redFlags: string[],
  inputs: ProfileInputs | null | undefined,
  program?: DentalProgram | null,
): string[] {
  const dat = extractDatScoreSummaryFromInputs(inputs);
  if (!dat) return redFlags;

  const schoolAverage = extractSchoolDatAverage(program);

  return redFlags.filter((flag) => {
    if (!isDatConcern(flag)) return true;
    if (/\bGPA\b/i.test(flag)) return true;
    if (preserveAsRetakeConcern(flag)) return true;

    const comparison = datAverageFromText(flag) ?? schoolAverage;
    if (comparison != null) {
      const applicantAverage = comparableApplicantAverage(dat, comparison);
      if (applicantAverage != null) return applicantAverage < comparison;
    }

    return !isGenerallyCompetitiveDat(dat);
  });
}
